import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { StatusBadge } from '@/components/submissions/StatusBadge';

import { AssignmentSection } from './AssignmentSection';
import { DecisionPanel } from './DecisionPanel';
import { PreviewRow } from './PreviewRow';
import { ShareSection } from './ShareSection';

import type { AssignmentInfo, SubmissionDetail, SubmissionStage } from '@/types/submission.types';

interface OverviewTabProps {
  detail: SubmissionDetail;
  onDecisionMade?: () => void;
}

export function OverviewTab({ detail, onDecisionMade }: OverviewTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<AssignmentInfo | undefined>(detail.assignment);

  const fullName = `${detail.firstName} ${detail.lastName}`;

  const handleAssigned = useCallback((newAssignment: AssignmentInfo) => {
    setAssignment(newAssignment);
  }, []);

  return (
    <div className='bg-white shadow rounded-lg p-6'>
      <div className='flex items-center gap-3 mb-6 border-b pb-4'>
        <h2 className='text-xl font-bold text-gray-900'>{fullName}</h2>
        <StatusBadge stage={detail.stage} />
      </div>
      <div className='text-sm text-gray-600 space-y-2'>
        <PreviewRow label={t('submissionDetail.overview.applicationId')} value={detail.applicationId} />
        <PreviewRow label={t('submissionDetail.overview.stage')} value={detail.stage} />
        <PreviewRow label={t('submissionDetail.overview.email')} value={detail.email} />
        <PreviewRow label={t('submissionDetail.overview.phone')} value={detail.phone} />
        <PreviewRow label={t('submissionDetail.overview.roles')} value={detail.roles.join(', ')} />
        <PreviewRow label={t('submissionDetail.overview.locations')} value={detail.locations.join(', ')} />
        <PreviewRow
          label={t('submissionDetail.overview.submittedAt')}
          value={new Date(detail.submittedAt).toLocaleDateString()}
        />
        <PreviewRow
          label={t('submissionDetail.overview.createdAt')}
          value={new Date(detail.createdAt).toLocaleDateString()}
        />
        <PreviewRow
          label={t('submissionDetail.overview.updatedAt')}
          value={new Date(detail.updatedAt).toLocaleDateString()}
        />
      </div>

      <AssignmentSection applicationId={detail.id} assignment={assignment} onAssigned={handleAssigned} />

      <ShareSection applicationId={detail.id} />

      <DecisionPanel
        applicationId={detail.id}
        stage={detail.stage}
        onDecisionMade={onDecisionMade ?? (Function.prototype as () => void)}
      />

      {detail.otherApplications && detail.otherApplications.length > 0 ? (
        <div className='mt-6 border-t pt-6'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>
            {t('submissionDetail.overview.otherApplications.title')} ({detail.otherApplications.length})
          </h3>
          <ul className='space-y-2'>
            {detail.otherApplications.map((app) => (
              <li key={app.applicationId}>
                <button
                  type='button'
                  onClick={() => void navigate(`/submissions/${app.applicationId}`)}
                  className='w-full text-left flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 hover:bg-gray-50 transition-colors'>
                  <div className='min-w-0'>
                    <p className='text-sm font-medium text-gray-900 truncate'>
                      {app.jobPostingTitle} ({app.jobPostingNumber})
                    </p>
                    <p className='text-xs text-gray-500 mt-0.5'>
                      {app.applicationNumber}
                      {app.submittedAt
                        ? ` · ${t('submissionDetail.overview.otherApplications.submitted')} ${new Date(app.submittedAt).toLocaleDateString()}`
                        : null}
                    </p>
                  </div>
                  <div className='ml-3 flex-shrink-0'>
                    <StatusBadge stage={app.status as SubmissionStage} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
